import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

interface PasswordRequirement {
  label: string;
  met: boolean;
}

interface PasswordStrengthIndicatorProps {
  password: string;
}

function getPasswordRequirements(password: string): PasswordRequirement[] {
  return [
    { label: 'At least 11 characters', met: password.length >= 11 },
    { label: 'At least 1 number', met: /[0-9]/.test(password) },
    {
      label: 'At least 1 special character',
      met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    },
    { label: 'At least 1 uppercase letter', met: /[A-Z]/.test(password) },
  ];
}

function getStrength(requirements: PasswordRequirement[]): {
  score: number;
  label: string;
  color: 'error' | 'warning' | 'success';
} {
  const metCount = requirements.filter((r) => r.met).length;
  const score = (metCount / requirements.length) * 100;

  if (metCount === requirements.length) {
    return { score, label: 'Strong', color: 'success' };
  } else if (metCount >= 2) {
    return { score, label: 'Medium', color: 'warning' };
  } else {
    return { score, label: 'Weak', color: 'error' };
  }
}

export default function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  if (!password) {
    return null;
  }

  const requirements = getPasswordRequirements(password);
  const strength = getStrength(requirements);

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Box sx={{ flexGrow: 1, mr: 1 }}>
          <LinearProgress
            variant='determinate'
            value={strength.score}
            color={strength.color}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
        <Typography variant='caption' color={`${strength.color}.main`} sx={{ minWidth: 50 }}>
          {strength.label}
        </Typography>
      </Box>
      <Box component='ul' sx={{ m: 0, pl: 0, listStyle: 'none' }}>
        {requirements.map((req) => (
          <Box
            component='li'
            key={req.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: req.met ? 'success.main' : 'text.secondary',
            }}
          >
            {req.met ? <CheckIcon sx={{ fontSize: 16 }} /> : <CloseIcon sx={{ fontSize: 16 }} />}
            <Typography variant='caption'>{req.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
